import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { z } from 'zod';

const FOCUS_TAGS = ['Diaspora', 'Investors', 'Youth', 'Academic', 'Government', 'Civil Society', 'Other'];
const STATUSES = ['upcoming', 'past', 'cancelled'];

const STATUS_VARIANT: Record<string, string> = {
  upcoming: 'bg-green-100 text-green-800',
  past: 'bg-secondary text-secondary-foreground',
  cancelled: 'bg-destructive/10 text-destructive',
};

const schema = z.object({
  title: z.string().min(1),
  city: z.string().min(1),
  country: z.string().min(1),
  venue_name: z.string().min(1),
  venue_address: z.string().min(1),
  date: z.string().min(1),
  start_time: z.string().min(1),
  end_time: z.string().min(1),
});

interface Event {
  id: string;
  title: string;
  city: string;
  country: string;
  venue_name: string;
  venue_address: string;
  date: string;
  start_time: string;
  end_time: string;
  description: string | null;
  focus_tags: string[] | null;
  registration_link: string | null;
  status: string | null;
  created_at: string | null;
}

const emptyForm = {
  title: '', city: '', country: '', venue_name: '', venue_address: '',
  date: '', start_time: '', end_time: '', description: '',
  focus_tags: [] as string[], registration_link: '', status: 'upcoming',
};

export function EventsTab() {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    setLoading(true);
    const { data } = await supabase.from('events').select('*').order('date', { ascending: false });
    setEvents((data as unknown as Event[]) || []);
    setLoading(false);
  };

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setErrors({}); setDialogOpen(true); };

  const openEdit = (e: Event) => {
    setEditingId(e.id);
    setForm({
      title: e.title, city: e.city, country: e.country,
      venue_name: e.venue_name, venue_address: e.venue_address,
      date: e.date, start_time: e.start_time, end_time: e.end_time,
      description: e.description || '',
      focus_tags: (e.focus_tags as string[]) || [],
      registration_link: e.registration_link || '',
      status: e.status || 'upcoming',
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const result = schema.safeParse(form);
    if (!result.success) {
      const e: Record<string, string> = {};
      result.error.issues.forEach(i => { e[i.path[0] as string] = i.message; });
      setErrors(e);
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title, city: form.city, country: form.country,
      venue_name: form.venue_name, venue_address: form.venue_address,
      date: form.date, start_time: form.start_time, end_time: form.end_time,
      description: form.description || null,
      focus_tags: form.focus_tags.length ? form.focus_tags : null,
      registration_link: form.registration_link || null,
      status: form.status,
    };
    let error;
    if (editingId) {
      ({ error } = await supabase.from('events').update(payload).eq('id', editingId));
    } else {
      ({ error } = await supabase.from('events').insert(payload));
    }
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success(editingId ? 'Event updated' : 'Event created');
    setDialogOpen(false);
    fetchEvents();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from('events').delete().eq('id', deleteId);
    if (error) { toast.error(error.message); return; }
    toast.success('Event deleted');
    setDeleteId(null);
    fetchEvents();
  };

  const toggleTag = (val: string) => {
    setForm(f => ({
      ...f,
      focus_tags: f.focus_tags.includes(val) ? f.focus_tags.filter(v => v !== val) : [...f.focus_tags, val],
    }));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t('admin.tabs.events')}</CardTitle>
        <Button onClick={openCreate} size="sm"><Plus className="mr-1 h-4 w-4" />{t('admin.addEvent')}</Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : events.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center">{t('events.noUpcoming')}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map(ev => (
                <TableRow key={ev.id}>
                  <TableCell className="font-medium">{ev.title}</TableCell>
                  <TableCell>{format(new Date(ev.date), 'MMM d, yyyy')}</TableCell>
                  <TableCell>{ev.city}, {ev.country}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_VARIANT[ev.status || 'upcoming'] || ''}>
                      {ev.status || 'upcoming'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(ev)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(ev.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Event' : t('admin.addEvent')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City *</Label>
                <Input value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                {errors.city && <p className="text-sm text-destructive">{errors.city}</p>}
              </div>
              <div>
                <Label>Country *</Label>
                <Input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
                {errors.country && <p className="text-sm text-destructive">{errors.country}</p>}
              </div>
            </div>
            <div>
              <Label>Venue Name *</Label>
              <Input value={form.venue_name} onChange={e => setForm(f => ({ ...f, venue_name: e.target.value }))} />
              {errors.venue_name && <p className="text-sm text-destructive">{errors.venue_name}</p>}
            </div>
            <div>
              <Label>Venue Address *</Label>
              <Input value={form.venue_address} onChange={e => setForm(f => ({ ...f, venue_address: e.target.value }))} />
              {errors.venue_address && <p className="text-sm text-destructive">{errors.venue_address}</p>}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                {errors.date && <p className="text-sm text-destructive">{errors.date}</p>}
              </div>
              <div>
                <Label>Start Time *</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))} />
                {errors.start_time && <p className="text-sm text-destructive">{errors.start_time}</p>}
              </div>
              <div>
                <Label>End Time *</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} />
                {errors.end_time && <p className="text-sm text-destructive">{errors.end_time}</p>}
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} />
            </div>
            <div>
              <Label>Focus Tags</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {FOCUS_TAGS.map(tag => (
                  <label key={tag} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.focus_tags.includes(tag)} onCheckedChange={() => toggleTag(tag)} />
                    {tag}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Registration Link</Label>
              <Input value={form.registration_link} onChange={e => setForm(f => ({ ...f, registration_link: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <Label>Status *</Label>
              <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete')}</AlertDialogTitle>
            <AlertDialogDescription>{t('admin.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
